import { useAtomValue } from "jotai/index";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { eventEmitter } from "@/eventEmitter";
import { contextMenuVisibilityAtom, contextMenuVisibilityItemsAtom } from "@/interface/AppStore";
import { getMenuItemsList } from "@/interface/ContextMenu/const";
import {
    getContextMenuPosition,
    getContextMenuSelectedPosition,
} from "@/interface/ContextMenu/helpers";
import { MenuItem } from "@/interface/ContextMenu/MenuItem";
import type { MenuItemsList, Position } from "@/interface/ContextMenu/types";
import styles from "./ContextMenu.module.css";

export const ContextMenu = () => {
    const { t } = useTranslation();
    const { isVisible, anchor } = useAtomValue(contextMenuVisibilityAtom);
    const contextMenuVisibilityItems = useAtomValue(contextMenuVisibilityItemsAtom);
    const menuItems = getMenuItemsList(t);

    const menuRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [openedSubmenu, setOpenedSubmenu] = useState<string | null>(null);

    useLayoutEffect(() => {
        if (!isVisible) {
            setOpenedSubmenu(null);
        }

        const menu = menuRef.current;
        if (!isVisible || !menu) return;

        if (anchor) {
            setPosition(getContextMenuSelectedPosition(menu, anchor));
        } else {
            setPosition(getContextMenuPosition(menu));
        }
    }, [isVisible, anchor]);

    const renderItems = (): MenuItemsList => {
        const items: MenuItemsList = [];

        menuItems.forEach((item, i) => {
            if (!contextMenuVisibilityItems.menuItems[item.name]) return;

            if (
                item.name === "addSticker" ||
                item.name === "delete" ||
                item.name === "undo" ||
                item.name === "copyAsPng" ||
                item.name === "toFront"
            ) {
                items.push({ id: 40 + i, name: "divider", label: "" });
            }

            if (item.name === "export" && !contextMenuVisibilityItems.menuItems["copyAsPng"]) {
                items.push({ id: 40 + i, name: "divider", label: "" });
            }

            items.push(item);
        });

        return items;
    };

    const handleMenuItemClick = (code: string) => {
        canv.contextMenu.callProcessorByCodeName?.(code);
        eventEmitter.dispatch("contextMenu.visibility", { isVisible: false, anchor: undefined });
    };

    if (!isVisible) return null;

    return (
        <div
            ref={menuRef}
            data-ui-root
            className={styles.contextMenu}
            style={{
                top: position.y,
                left: position.x,
            }}
        >
            {renderItems().map((item) => (
                <MenuItem
                    key={item.id}
                    item={item}
                    onClick={handleMenuItemClick}
                    openedSubmenu={openedSubmenu}
                    setOpenedSubmenu={setOpenedSubmenu}
                />
            ))}
        </div>
    );
};
